"use client";

import { useContactModal } from "@/components/ContactModalProvider";
import { Container } from "@/components/ui/Container";
import styles from "./infrastructure-audits.module.css";

/*
  Both request CTAs were bare <div>/<img> pairs driven by Webflow's interactions
  engine — not focusable, no accessible name, no keyboard path to the form. They
  are real buttons here, opening the same React modal; the artwork is the same
  SVG the live page uses, and its alt text is the button's accessible name.
*/

const highlights = [
  { icon: "/images/homepage-countries.svg", stat: "60+", label: "Ecosystems" },
  { icon: "/images/homepage-secured.svg", stat: "$500B+", label: "Digital Assets Secured" },
  {
    icon: "/images/homepage-audits.svg",
    stat: "1300+",
    label: "Audits Conducted",
    href: "https://certificate.quantstamp.com/",
  },
];

const capabilities = [
  { icon: "/images/microchip.svg", label: "API Security Testing" },
  { icon: "/images/blog.svg", label: "Browser Extensions" },
  { icon: "/images/cloud-word.svg", label: "Cloud" },
  { icon: "/images/infrastructure-audits.svg", label: "Infrastructure" },
  { icon: "/images/file-shield.svg", label: "Mobile Applications" },
  { icon: "/images/web-app-security.svg", label: "Web Applications" },
];

const valueProps = [
  {
    heading: "Protects the backbone of Web3",
    body: "Web3 consists of smart contracts and the Web2 architecture around them. A single misconfigured server, API, or cloud component can undermine even the most secure smart contracts, exposing users and funds to risk.",
  },
  {
    heading: "Ensures availability, reliability, and AI integrity",
    body: "Security measures tailored for the intersection of Web2 + Web3 protect against downtime, DDoS, and other attacks, while also safeguarding AI-powered services that increasingly drive decision-making, automation, and user interaction.",
  },
  {
    heading: "Strengthens security and compliance posture",
    body: "By addressing risks unique to the intersection of Web2 infrastructure, Web3 logic, and AI integrations, projects close critical gaps attackers exploit while aligning with regulatory and industry compliance requirements.",
  },
];

/* The page uses wider crops of the same client marks the request modal shows. */
const clientLogos = [
  { src: "/images/republic.svg", alt: "Republic", modifier: styles.republic },
  { src: "/images/liquid-collective.png", alt: "Liquid Collective", modifier: styles.collective },
  { src: "/images/galxe2x.svg", alt: "GALXE" },
  { src: "/images/kiln.svg", alt: "Kiln", modifier: styles.kiln },
  { src: "/images/luganodes.svg", alt: "Luganodes", modifier: styles.luganodes },
  { src: "/images/twinstake.svg", alt: "Twinstake" },
  { src: "/images/rockx.svg", alt: "RockX", modifier: styles.rockx },
  { src: "/images/contribution-dao.png", alt: "Contribution DAO", modifier: styles.contribution },
];

export function InfrastructureAuditsContent() {
  const { open } = useContactModal();

  return (
    <>
      <div className={styles.hero}>
        <Container padding="none" className={styles.heroInner}>
          <h1 className={styles.heroTitle}>Comprehensive Infrastructure Penetration Testing</h1>
          <p className={styles.heroLead}>A Complementary Approach to ISO 27001 and SOC 2</p>
          <button type="button" className={`${styles.cta} ${styles.heroCta}`} onClick={() => open("pentest")}>
            <img src="/images/Button.svg" alt="Request a Pentest" className={styles.ctaImage} />
          </button>
        </Container>
      </div>

      <div className={styles.highlightsWrapper}>
        <Container as="section" className={styles.highlights} aria-label="Quantstamp by the numbers">
          {highlights.map((item) => (
            <div key={item.label} className={styles.highlight}>
              <img src={item.icon} alt="" className={styles.highlightIcon} />
              {item.href ? (
                <a href={item.href} target="_blank" rel="noreferrer" className={styles.highlightStatLink}>
                  {item.stat}
                </a>
              ) : (
                <div className={styles.highlightStat}>{item.stat}</div>
              )}
              <div className={styles.highlightLabel}>{item.label}</div>
            </div>
          ))}
        </Container>
      </div>

      <Container as="section" className={styles.body}>
        <div className={styles.column}>
          <h2 className={styles.sectionHeading}>Web3 Infrastructure Audits</h2>

          <ul className={styles.capabilities}>
            {capabilities.map((item) => (
              <li key={item.label} className={styles.capability}>
                <img src={item.icon} alt="" />
                <span>{item.label}</span>
              </li>
            ))}
          </ul>

          {valueProps.map((prop) => (
            <p key={prop.heading} className={styles.valueProp}>
              <strong>{prop.heading}</strong> — {prop.body}
            </p>
          ))}

          <div className={styles.clients}>
            <h2 className={styles.sectionHeading}>Trusted by Leading Validators and Applications</h2>
            <ul className={styles.clientLogos}>
              {clientLogos.map((logo) => (
                <li key={logo.src}>
                  <img src={logo.src} alt={logo.alt} className={[styles.clientLogo, logo.modifier].filter(Boolean).join(" ")} />
                </li>
              ))}
            </ul>
          </div>

          <hr className={styles.rule} />

          <ul className={styles.checks}>
            <li>
              <img src="/images/solid-sharp-check-shield-1.svg" alt="" />
              <span>
                A security review of your project’s infrastructure using both automated scanning and manual penetration
                testing techniques
              </span>
            </li>
            <li>
              <img src="/images/solid-sharp-check-shield-1.svg" alt="" />
              <span>
                Infrastructure penetration tests are relevant for enterprises of all sizes, as well as cloud providers,
                node operators, SaaS platforms, APIs, and any system with critical online off-chain components
              </span>
            </li>
            <li>
              <img src="/images/solid-sharp-check-shield-1.svg" alt="" />
              <span>
                A Quantstamp infrastructure penetration test helps your organization demonstrate a higher standard of
                security, positioning you as a trusted provider in competitive markets
              </span>
            </li>
            <li>
              <img src="/images/solid-sharp-check-shield-1.svg" alt="" />
              <span>
                By completing a Quantstamp infrastructure audit, node operators can secure{" "}
                <a
                  href="https://docs.stakingrewards.com/verified-provider-program/vsp-pro-or-audited-by-quantstamp/a-collaboration-between-staking-rewards-and-quantstamp"
                  target="_blank"
                  rel="noreferrer"
                >
                  Verified Staking Provider (VSP) Pro
                </a>{" "}
                status on Staking Rewards and stand out from hundreds of competing providers.
              </span>
            </li>
          </ul>
        </div>
      </Container>

      <div className={styles.closing}>
        <Container padding="none" className={styles.closingInner}>
          <p className={styles.closingText}>
            Ready to protect your off-chain code against configuration errors and external attacks?
          </p>
          <button type="button" className={styles.cta} onClick={() => open("pentest")}>
            Request a Pentest
          </button>
        </Container>
      </div>
    </>
  );
}
